import React, { useMemo } from "react";
import { Overlay, BaseOverlaySchema, AnimationType } from "../Overlay";
import { interpolate, Easing, useCurrentFrame } from "remotion";
import { z } from "zod";

// Quotation mark SVG component
const QuotationIcon = ({ size = 40, color = "#FFFFFF" }) => (
<svg fill={color} width={size} height={size} viewBox="0 -5 34 34">
<path d="m31.2 0h-7.2l-4.8 9.6v14.4h14.4v-14.4h-7.2zm-19.2 0h-7.2l-4.8 9.6v14.4h14.4v-14.4h-7.2z"/>
</svg>
);

// Helper function to wrap text into lines based on container width and font size
const wrapTextIntoLines = (text, maxWidth, fontSize) => {
  const avgCharWidth = fontSize * 0.55; // Average character width estimate
  const charsPerLine = Math.floor(maxWidth / avgCharWidth);
  
  if (charsPerLine <= 0) return [text]; // Fallback if width is too small
  
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';
  
  words.forEach((word) => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length <= charsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  
  if (currentLine) lines.push(currentLine);
  return lines;
};

// Extend base schema with ParagraphDisplay-specific properties
export const Schema = BaseOverlaySchema.extend({
  paragraphText: z.string().default("A minimalist white kitchen with wooden accents, full appliances, and a refreshing green view.").describe("Paragraph text content"),
  quoteBackgroundColor: z.string().default("#4FB3A3").describe("Quote icon background color (hex)"),
  iconSize: z.number().default(40).describe("Quote icon size in pixels"),
  maxWidth: z.number().default(720).describe("Maximum width for paragraph text in pixels"),
  containerWidth: z.number().default(1080).describe("Container width in pixels"),
  screenHeight: z.number().default(1920).describe("Screen height for calculating bottom third positioning"),
  textStartFrame: z.number().default(0).describe("Frame when text appears"),
  quoteStartFrame: z.number().default(30).describe("Frame when quote background fade starts"),
  quoteFadeDuration: z.number().default(25).describe("Duration of quote background fade in frames"),
  iconAppearFrame: z.number().default(50).describe("Frame when quote icon appears"),
  fontSize: z.number().optional().describe("Paragraph font size in pixels (optional - auto-calculated if not provided)"),
  fontWeight: z.string().default("400").describe("Font weight"),
  lineHeight: z.number().default(1.6).describe("Line height multiplier"),
});

export const ParagraphDisplay = ({
  // Base overlay props
  startFrame = 0,
  endFrame = 300,
  inAnimation = AnimationType.FADE,
  inDuration = 20,
  outAnimation = AnimationType.FADE,
  outDuration = 15,
  x = 100,
  y = undefined, // Will be calculated to bottom third if not provided
  opacity = 1,
  zIndex = 10,
  // ParagraphDisplay-specific props
  paragraphText = "A minimalist white kitchen with wooden accents, full appliances, and a refreshing green view.",
  quoteBackgroundColor = "#4FB3A3",
  iconSize = 50,
  maxWidth = 920,
  containerWidth = 1080,
  screenHeight = 1920,
  textStartFrame = 0,
  quoteStartFrame = 30,
  quoteFadeDuration = 25,
  iconAppearFrame = 50,
  fontSize = 30,
  fontWeight = "600",
  lineHeight = 1,
}) => {
  const currentFrame = useCurrentFrame();

  // Constants
  const MIN_FONT_SIZE = 18;
  const FONT_HEIGHT_RATIO = 0.05;
  const ICON_PADDING = 30; // Padding around quote icon
  const ICON_TO_TEXT_SPACING = 16; // Vertical gap between icon and first text line

  // Calculate y position to be in bottom third if not provided
  const calculatedY = useMemo(() => {
    if (y !== undefined) return y;
    // Bottom third starts at 2/3 of screen height
    return Math.round(screenHeight * (2 / 3));
  }, [y, screenHeight]);
  const calculatedFontSize = useMemo(() => {
    if (fontSize) return fontSize;
    // Responsive sizing: roughly 5% of maxWidth height
    return Math.max(MIN_FONT_SIZE, Math.round(maxWidth * FONT_HEIGHT_RATIO));
  }, [fontSize, maxWidth]);

  // Calculate frame positions relative to overlay start
  const frameInOverlay = useMemo(() => {
    return currentFrame - startFrame;
  }, [currentFrame, startFrame]);

  // Text visibility: appears immediately at textStartFrame
  const textOpacity = useMemo(() => {
    if (frameInOverlay < textStartFrame) return 0;
    return 1;
  }, [frameInOverlay, textStartFrame]);

  // Quote background fade: starts at quoteStartFrame and fades in over quoteFadeDuration
  // Fades from top to bottom using clip-path
  const quoteFadeProgress = useMemo(() => {
    const progressStart = frameInOverlay - quoteStartFrame;
    return Math.min(1, Math.max(0, progressStart / quoteFadeDuration));
  }, [frameInOverlay, quoteStartFrame, quoteFadeDuration]);

  // Quote icon opacity: appears after quote background is mostly revealed (at ~70% fade)
  const iconOpacity = useMemo(() => {
    if (quoteFadeProgress < 0.7) return 0;
    // Fade in icon after 70% of quote background fade is complete
    const iconFadeStart = 0.7;
    return interpolate(
      quoteFadeProgress,
      [iconFadeStart, 1],
      [0, 1],
      {
        easing: Easing.out(Easing.cubic),
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      }
    );
  }, [quoteFadeProgress]);

  // Calculate quote background dimensions
  const quoteBackgroundHeight = (iconSize + ICON_PADDING * 2) / 2;
  const quoteBackgroundWidth = iconSize + ICON_PADDING * 2;

  const wrappedLines = useMemo(
    () => wrapTextIntoLines(paragraphText, maxWidth, calculatedFontSize),
    [paragraphText, maxWidth, calculatedFontSize]
  );

  return (
    <Overlay
      startFrame={startFrame}
      endFrame={endFrame}
      inAnimation={inAnimation}
      inDuration={inDuration}
      outAnimation={outAnimation}
      outDuration={outDuration}
      x={x}
      y={calculatedY}
      opacity={opacity}
      zIndex={zIndex}
      style={{
        position: "relative",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateRows: `${quoteBackgroundHeight}px auto`,
          rowGap: `${ICON_TO_TEXT_SPACING}px`,
          width: `${maxWidth}px`,
        }}
      >
        {/* Quote background with icon container - occupies first grid row */}
        <div
          style={{
            position: "relative",
            width: quoteBackgroundWidth,
            height: quoteBackgroundHeight,
          }}
        >
          {/* Teal background - fades in from top to bottom */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: quoteBackgroundColor,
              opacity: quoteFadeProgress,
              clipPath: `inset(0 0 ${100 - quoteFadeProgress * 100}% 0)`,
              zIndex: 5,
            }}
          />

          {/* Quote icon - appears after teal background is mostly revealed */}
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: iconOpacity,
              zIndex: 6,
            }}
          >
            <QuotationIcon size={iconSize} color="#FFFFFF" />
          </div>
        </div>

        {/* Paragraph text with white background - occupies second grid row */}
        <div
          style={{
            opacity: textOpacity,
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            maxWidth: `${maxWidth}px`,
          }}
        >
          {wrappedLines.map((line, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "#FFFFFF",
                padding: "6px 12px",
                borderRadius: "4px",
                display: "inline-block",
                width: "fit-content",
              }}
            >
              <span
                style={{
                  fontFamily: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: fontWeight,
                  fontSize: `${calculatedFontSize}px`,
                  lineHeight: lineHeight,
                  color: "#000000",
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>
      </div>
    </Overlay>
  );
};

export default ParagraphDisplay;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
